import { type BalanceSheetId, type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { balanceSheetsQueryKey } from "./financials.keys";
import { patchBalanceSheetView } from "./utils/patchBalanceSheetView";

export function useUpdateBalanceSheet(accountId: SuretyAccountId) {
	const { patch } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["updateBalanceSheet", accountId],
		mutationFn: ({
			balanceSheetId,
			body,
		}: {
			balanceSheetId: BalanceSheetId;
			body: Dtos.Admin.BalanceSheet.Update.Request;
		}) =>
			patch<Dtos.Admin.BalanceSheet.Get.Response>(
				`/v2/admin/surety/accounts/${accountId}/balance-sheets/${balanceSheetId}/line-items`,
				body,
			),
		onMutate: async ({ balanceSheetId, body }) => {
			await queryClient.cancelQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
			);
			queryClient.setQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
				(cached) =>
					cached?.map((s) =>
						s.id === balanceSheetId && s.view
							? { ...s, view: patchBalanceSheetView(s.view, body) }
							: s,
					),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(balanceSheetsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (queryClient.isMutating({ mutationKey: ["updateBalanceSheet", accountId] }) <= 1) {
				queryClient.invalidateQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			}
		},
	});

	return {
		updateBalanceSheet: mutation.mutateAsync,
	};
}
